import React, { useCallback, useEffect, useState, useMemo, useContext, ChangeEvent } from 'react'
import makeStyles from '@mui/styles/makeStyles';

import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import Select from '@mui/material/Select'
import Box from '@mui/material/Box'

import {
  convertFromRaw,
} from 'draft-js'

import {
  ILayout_Editor,
  ILayout_Cell_Props_Image,
  ILayout_Cell_Props_Image_SizeField,
  ILayout_Cell_Props_Image_SizeUnit,
} from '@kaidam/fc-shared/src/types/layout'

import {
  ITextEditor_Link,
} from '@kaidam/fc-shared/src/types/texteditor'

import {
  IForm_File,
} from '@kaidam/fc-shared/src/types/schema/form'

import TinyButton from '../../widgets/TinyButton'
import icons from '../../../icons'

import FileChooser from '../../filestore/FileChooser'

import EditorContext from '../EditorContext'

import {
  LAYOUT_IMAGE_SIZE_FIELDS,
  LAYOUT_IMAGE_SIZE_UNITS,
  LAYOUT_IMAGE_SIZE_FIELD_DEFAULT,
  LAYOUT_IMAGE_SIZE_UNIT_DEFAULT,
  LAYOUT_IMAGE_SIZE_AMOUNT_DEFAULT,
} from '@kaidam/fc-shared/src/constants'

import LinkEditor from '../../texteditor/LinkEditor'

import {
  flattenLink,
} from '@kaidam/fc-shared/src/texteditor/utils'

/*

  EDITOR

*/
const useStyles = makeStyles(theme => ({
  
}))

const ImageEditor: ILayout_Editor<ILayout_Cell_Props_Image> = ({
  cell,
  triggerEdit,
  onSetEditing,
  onDeselect,
  onChange,
}) => {

  const editorContext = useContext(EditorContext)
  const classes = useStyles()

  const [ choosing, setChoosing ] = useState(false)
  const [ editingLink, setEditingLink ] = useState(false)
  const [ numericValue, setNumericValue ] = useState('')

  const {
    sizeField = LAYOUT_IMAGE_SIZE_FIELD_DEFAULT,
    sizeAmount = LAYOUT_IMAGE_SIZE_AMOUNT_DEFAULT,
    sizeUnit = LAYOUT_IMAGE_SIZE_UNIT_DEFAULT,
    link,
    caption = '',
  } = cell.data

  useEffect(() => {
    setNumericValue(sizeAmount.toString())
  }, [
    sizeAmount,
  ])

  const hasCurrentLink = link ? true : false

  const onEdit = useCallback(() => {
    setChoosing(true)
    onSetEditing(true)
  }, [])

  const onEditLink = useCallback(() => {
    setEditingLink(true)
    onSetEditing(true)
  }, [])

  const onCancelEdit = useCallback(() => {
    setChoosing(false)
    onSetEditing(false)
    onDeselect()
  }, [])

  const onCancelEditLink = useCallback(() => {
    setEditingLink(false)
    onSetEditing(false)
  }, [])

  const onResetLink = useCallback(() => {
    onChange({
      link: undefined,
    })
    setEditingLink(false)
    onSetEditing(false)
  }, [
    onChange,
  ])

  const onUpdateLink = useCallback((link: ITextEditor_Link) => {
    onChange({
      link,
    })
    setEditingLink(false)
    onSetEditing(false)
  }, [
    onChange,
  ])

  const onUpdateCaption = useCallback((e: any) => {
    onChange({
      caption: e.target.value,
    })
  }, [
    onChange,
  ])

  const onChooseFile = useCallback(async (file: IForm_File) => {
    let update: Partial<ILayout_Cell_Props_Image> = {
      file,
    }
    onChange(update)
    setChoosing(false)
    onSetEditing(false)
  }, [
    onChange,
  ])

  const onUpdateSizeField = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const sizeField = event.target.value as ILayout_Cell_Props_Image_SizeField
    onChange({
      sizeField,
    })
    onSetEditing(false)
  }, [
    onChange,
  ])

  const onUpdateSizeUnit = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const sizeUnit = event.target.value as ILayout_Cell_Props_Image_SizeUnit
    onChange({
      sizeUnit,
    })
    onSetEditing(false)
  }, [
    onChange,
  ])

  const onUpdateSizeAmount = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setNumericValue(event.target.value as string)
    const sizeAmount = parseFloat(event.target.value as string)
    if(!isNaN(sizeAmount)) {
      onChange({
        sizeAmount,
      })
    }
    onSetEditing(false)
  }, [
    onChange,
  ])

  const textUrl = useMemo<string>(() => {
    if(!link?.url) return '#'
    return flattenLink(link)
  }, [
    link,
  ])

  useEffect(() => {
    if(triggerEdit) onEdit()
  }, [
    triggerEdit,
  ])

  return (
    <Box>
      <TinyButton
        size="small"
        color="primary"
        onClick={ onEdit }
      >
        Find Image
      </TinyButton>
      <Divider
      sx={{
        height: '100%',
      }}
      />
      <Box
        className='settingsRow'
        sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            '& .shrink': {
              flexGrow: 0,
              mx: 1,
            },
            '& .grow': {
              flexGrow: 1,
              '& input': {
                textAlign: 'right',
              }
            },
            '& .caption': {
              flexGrow: 1,
              msTextAutospace: 1,
              mx: 1,
            },
        }}
        onMouseEnter={ () => {
          onSetEditing(true)
        }}
        onMouseLeave={ () => {
          onSetEditing(false)
        }}
      >
        <Box className="shrink">
          <FormControl
            variant='standard'
          >
            <Select
              value={ sizeField }
              onChange={ onUpdateSizeField }
            >
              {
                LAYOUT_IMAGE_SIZE_FIELDS.map(option => (
                  <MenuItem key={ option } value={ option }>{ option }</MenuItem>
                ))
              }
            </Select>
          </FormControl>
        </Box>
        <Box className="grow">
          <TextField
            fullWidth
            variant='standard'
            type="number"
            value={ numericValue }
            onChange={ onUpdateSizeAmount }
          />
        </Box>
        <Box className="shrink">
          <FormControl
            variant='standard'
          >
            <Select
              value={ sizeUnit }
              onChange={ onUpdateSizeUnit }
            >
              {
                LAYOUT_IMAGE_SIZE_UNITS.map(option => (
                  <MenuItem key={ option } value={ option }>{ option }</MenuItem>
                ))
              }
            </Select>
          </FormControl>
        </Box>
      </Box>
      <Box
        className='settingsRow'
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          '& .shrink': {
            flexGrow: 0,
            mx: 1,
          },
          '& .grow': {
            flexGrow: 1,
            '& input': {
              textAlign: 'right',
            }
          },
          '& .caption': {
            flexGrow: 1,
            mt: 1,
            mx: 1,
          },
        }}
        onMouseEnter={ () => {
          onSetEditing(true)
        }}
        onMouseLeave={ () => {
          onSetEditing(false)
        }}
      >
        <Box className="caption">
          <TextField
            fullWidth
            variant='standard'
            label="Caption"
            value={ caption }
            onChange={ onUpdateCaption }
          />
        </Box>
      </Box>
      <Divider
        sx={{
          height: '100%',
        }}
      />
      {
        hasCurrentLink && link && (
          <Box
            sx={{
              height: '100%',
            }}
          >
            <Typography
              variant="caption"
              className='linkTitle'
              sx={{
                color: theme => theme.palette.grey[500],
              }}
            >
              { textUrl }
            </Typography>
          </Box>
        )
      }
      <TinyButton
        size="small"
        sx={{
          height: '100%',
        }}
        onClick={ onEditLink }
      >
        Edit Link
      </TinyButton>
      {
        choosing && (
          <FileChooser
            publicMode={ editorContext.options.publicFiles ? true : false }
            sharedMode={ editorContext.options.sharedFiles ? true : false }
            displayTypes={['image', 'folder']}
            selectTypes={['image']}
            onChoose={ onChooseFile }
            onClose={ onCancelEdit }
          />
        )
      }
      {
        editingLink && (
          <LinkEditor
            value={ link }
            enabledMagicTags={ editorContext.options.linkMagicTags }
            linkTypes={ editorContext.options.linkTypes }
            publicFiles={ editorContext.options.publicFiles }
            sharedFiles={ editorContext.options.sharedFiles }
            onSave={ onUpdateLink }
            onReset={ onResetLink }
            onCancel={ onCancelEditLink }
          />
        )
      }
    </Box>
  )
}

export default ImageEditor